// SPEC-LOGIN-BOUNCE-AFTER-SUCCESS-01 (D-838) 一: ログインの足跡。
//
// ★なぜ要るか
//   「ログインに成功したのに /login へ押し戻される」が 21 日で約 120 回起きているのに、
//   client 側の出来事がどこにも残っていない(Sentry の本番 DSN が無い)。
//   どのガードがどの状態を見て戻したのかが分からないまま直すと、D-733 の二の舞になる
//   (直したが 21 日で 120 回のまま = 直した場所が原因の全部ではなかった)。
//
// ★設計の要点
//   ・ログイン前(anon)は **送らない**。端末(localStorage)に貯める。
//     未ログインから書ける経路を作らないため(spec FORBIDDEN)。
//   ・ログイン成功後(authenticated)に 1 回だけまとめて送る。
//   ・残すのは出来事名・理由コード・真偽値・版・端末の粗い種別だけ。
//     ★個人情報 / トークン / PIN は **引数にも入れない**(入れる口を作らない)。
//   ・staff_id は送らない。サーバが JWT から取る(なりすまし不可)。
//   ・失敗しても絶対にログインを止めない(全部 try/catch で飲む)。

const KEY = 'd838_auth_trace'
const MAX = 50            // サーバ側も 50 で弾く。端末側でも同じ上限にする
const VERSION = (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '')

/** 出来事。★ここに無い名前はサーバが捨てる(両側で決め打ち)。 */
export const TRACE = {
  pinSubmit:       'pin_submit',
  verifyOk:        'verify_ok',
  setSession:      'set_session',
  launcherReached: 'launcher_reached',
  guardRedirect:   'guard_redirect',
  pageLoad:        'page_load',
  authState:       'auth_state',
  revalidatePurge: 'revalidate_purge',
}

/** 端末の粗い種別。機種名は取らない(個人の特定に近づけない)。 */
function deviceKind() {
  try {
    const ua = navigator.userAgent || ''
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
    if (/Android/i.test(ua)) return 'android'
    if (/Windows|Macintosh|X11|Linux/i.test(ua)) return 'pc'
  } catch { /* ignore */ }
  return 'other'
}

function read() {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]') } catch { return [] }
}
function write(list) {
  try { localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX))) } catch { /* ignore */ }
}

/**
 * 1 回のログイン試行をまとめる ID。
 * ★ページを再読込しても同じ試行として繋がるよう localStorage に置く。
 *   そうしないと「押し戻し → 再読込 → 再ログイン」が別々の話に見えて、繋がりが読めない。
 */
export function currentTraceId() {
  try {
    let id = localStorage.getItem(KEY + '_id')
    if (!id) {
      id = (crypto?.randomUUID?.() ?? String(Math.random())).slice(0, 36)
      localStorage.setItem(KEY + '_id', id)
    }
    return id
  } catch { return 'no-storage' }
}

/** ログインが完全に成立したら次の試行へ移す(足跡が延々と繋がらないように)。 */
export function resetTraceId() {
  try { localStorage.removeItem(KEY + '_id') } catch { /* ignore */ }
}

/**
 * 足跡を 1 つ貯める(この時点では送らない)。
 * @param {string} event TRACE のいずれか
 * @param {string|null} reason 英数と - _ だけの理由コード。★自由文・氏名・PIN を入れない
 * @param {object} detail 真偽値だけ。★ここに個人情報を入れる余地を作らない
 */
export function traceAuth(event, reason = null, detail = {}) {
  try {
    const list = read()
    list.push({
      trace_id: currentTraceId(),
      seq: list.length,
      event,
      reason: reason ?? null,
      // ★決め打ちのキーだけ。呼び出し側が何を渡しても、ここで真偽値に潰す。
      detail: {
        loading:      detail.loading      === undefined ? undefined : !!detail.loading,
        isLoggedIn:   detail.isLoggedIn   === undefined ? undefined : !!detail.isLoggedIn,
        offline:      detail.offline      === undefined ? undefined : !!detail.offline,
        hasSession:   detail.hasSession   === undefined ? undefined : !!detail.hasSession,
        hasCached:    detail.hasCached    === undefined ? undefined : !!detail.hasCached,
        sessionError: detail.sessionError === undefined ? undefined : !!detail.sessionError,
      },
      app_version: VERSION,
      device_kind: deviceKind(),
      occurred_at: new Date().toISOString(),
    })
    write(list)
  } catch { /* 計測はログインを絶対に止めない */ }
}

/**
 * 貯まった足跡を送る。★authenticated のときだけ呼ぶこと(呼び出し側の責任)。
 * 送れたら端末から消す。送れなければ残しておいて次回に回す。
 */
export async function flushAuthTrace(supabase) {
  try {
    const list = read()
    if (!list.length) return 0
    const { error } = await supabase.rpc('fn_log_auth_trace', { p_events: list })
    if (error) return 0        // ★消さない。次のログインでもう一度試す
    write([])
    return list.length
  } catch {
    return 0                   // ★失敗してもログインを止めない
  }
}
